import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Layers,
  LayoutList,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  X,
  Pause,
  Square,
  CheckSquare,
  Minus,
  Terminal,
  MessageSquare,
  Workflow,
  FolderOpen,
  Focus,
  Play,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { AgentTypeAvatar, AGENT_TYPE_CONFIG } from "@/components/colony/AgentTypeAvatar";
import type { AgentType } from "@/components/colony/AgentTypeAvatar";
import { useUnifiedAgents } from "@/hooks/useUnifiedAgents";
import type {
  UnifiedAgentRow,
  UnifiedAgentGroup,
  AgentSource,
} from "@/lib/data/unified-agents";
import {
  STATUS_CONFIG,
  type UnifiedAgentStatus,
} from "@/lib/utils/agent-status";
import { PROVIDER_META, type AiProvider } from "@/types/ai-chat";
import { ACTION_HANDLERS, setAgentActionNavigator } from "@/lib/data/agent-actions";
import { useWorldStore } from "@/stores/worldStore";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useChatTabStore } from "@/stores/chatTabStore";
import { ChatConversation } from "@/features/factory/ai-chat/ChatConversation";
import { ChatInput } from "@/features/factory/ai-chat/ChatInput";
import type { ChatAttachment } from "@/types/content-blocks";
import { useLocation } from "wouter";

// ─── Types ──────────────────
type GroupBy = "none" | "status" | "project" | "source" | "agent-type";

// ─── Agent Type Config ──────────────────
const AGENT_LABELS: Record<AgentType, string> = Object.fromEntries(
  Object.entries(AGENT_TYPE_CONFIG).map(([k, v]) => [k, v.label])
) as Record<AgentType, string>;

// ─── Source Config ──────────────────
const SOURCE_CONFIG: Record<AgentSource, { label: string; Icon: LucideIcon }> = {
  workflow: { label: "Workflow", Icon: Workflow },
  chat: { label: "Chat", Icon: MessageSquare },
  terminal: { label: "Terminal", Icon: Terminal },
};

const STATUS_ORDER: UnifiedAgentStatus[] = ["running", "waiting_review", "idle", "error", "completed", "stale"];

// ─── Format relative time ──────────────────
function formatRelativeTime(ts: number | null): string {
  if (!ts) return "—";
  const diff = Math.floor((Date.now() - ts) / 1000 / 60);
  if (diff < 1) return "now";
  if (diff < 60) return `${diff}m`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  const days = Math.floor(diff / 1440);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

function formatCost(costUsd: number | undefined): string {
  if (costUsd == null || costUsd === 0) return "$0.00";
  return `$${costUsd.toFixed(2)}`;
}

// ─── Grouping helpers ──────────────────
function groupRows(
  groups: UnifiedAgentGroup[],
  groupBy: GroupBy,
): { key: string; label: string; rows: UnifiedAgentRow[] }[] {
  const allRows = groups.flatMap((g) => g.rows);

  if (groupBy === "none") {
    return [{ key: "all", label: "", rows: allRows }];
  }

  if (groupBy === "project") {
    return groups
      .filter((g) => g.rows.length > 0)
      .map((g) => ({
        key: String(g.projectId ?? "ungrouped"),
        label: g.projectName,
        rows: g.rows,
      }));
  }

  if (groupBy === "status") {
    const result: { key: string; label: string; rows: UnifiedAgentRow[] }[] = [];
    for (const status of STATUS_ORDER) {
      const items = allRows.filter((r) => r.status === status);
      if (items.length > 0) {
        result.push({ key: status, label: STATUS_CONFIG[status].label, rows: items });
      }
    }
    return result;
  }

  if (groupBy === "source") {
    const result: { key: string; label: string; rows: UnifiedAgentRow[] }[] = [];
    for (const source of ["workflow", "chat", "terminal"] as AgentSource[]) {
      const items = allRows.filter((r) => r.source === source);
      if (items.length > 0) {
        result.push({ key: source, label: SOURCE_CONFIG[source].label, rows: items });
      }
    }
    return result;
  }

  if (groupBy === "agent-type") {
    const map = new Map<string, UnifiedAgentRow[]>();
    for (const r of allRows) {
      const key = r.agentType ?? r.source;
      const arr = map.get(key);
      if (arr) arr.push(r);
      else map.set(key, [r]);
    }
    const result: { key: string; label: string; rows: UnifiedAgentRow[] }[] = [];
    map.forEach((items, key) => {
      result.push({
        key,
        label: AGENT_LABELS[key as AgentType] ?? SOURCE_CONFIG[key as AgentSource]?.label ?? key,
        rows: items,
      });
    });
    return result;
  }

  return [{ key: "all", label: "", rows: allRows }];
}

// ─── Status Dot ──────────────────
function StatusDot({ status }: { status: UnifiedAgentStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <div className="flex items-center gap-1.5" title={config.label}>
      <div className={`w-2 h-2 rounded-full ${config.dotClass}`} />
    </div>
  );
}

// ─── Source Badge ──────────────────
function SourceBadge({ source }: { source: AgentSource }) {
  const { Icon } = SOURCE_CONFIG[source];
  return (
    <div className="flex items-center justify-center" title={SOURCE_CONFIG[source].label}>
      <Icon size={14} strokeWidth={1.5} className="text-colony-text-tertiary" />
    </div>
  );
}

// ─── Agent Column Cell ──────────────────
function AgentCell({ row }: { row: UnifiedAgentRow }) {
  if (row.source === "workflow" && row.agentType) {
    return (
      <div className="flex items-center gap-1.5">
        <AgentTypeAvatar type={row.agentType} size={18} />
        <span className="text-[11px] text-colony-text-secondary truncate">
          {AGENT_LABELS[row.agentType]}
        </span>
      </div>
    );
  }

  if (row.source === "chat" && row.provider) {
    const meta = PROVIDER_META[row.provider];
    return (
      <div className="flex items-center gap-1.5">
        <img src={meta.logo} alt={meta.label} className="w-[18px] h-[18px] rounded-[3px] object-contain" />
        <span className="text-[11px] text-colony-text-secondary truncate">
          {meta.label}
        </span>
      </div>
    );
  }

  if (row.source === "terminal" && row.cwd) {
    const basename = row.cwd.split("/").pop() ?? row.cwd;
    return (
      <div className="flex items-center gap-1.5">
        <Terminal size={14} strokeWidth={1.5} className="text-colony-text-tertiary" />
        <span className="text-[11px] text-colony-text-secondary truncate font-mono">
          {basename}
        </span>
      </div>
    );
  }

  return (
    <span className="text-[11px] text-colony-text-tertiary">—</span>
  );
}

// ─── Detail Panel ──────────────────
function DetailPanel({ row, onClose, onDelete }: { row: UnifiedAgentRow; onClose: () => void; onDelete: () => void }) {
  const statusCfg = STATUS_CONFIG[row.status];
  const sessions = useAiChatStore((s) => s.sessions);
  const sendMessage = useAiChatStore((s) => s.sendMessage);
  const stopSession = useAiChatStore((s) => s.stopSession);
  const createSession = useAiChatStore((s) => s.createSession);
  const chatTabs = useChatTabStore((s) => s.tabs);
  const linkSession = useChatTabStore((s) => s.linkSession);
  const renameTab = useChatTabStore((s) => s.renameTab);
  const projects = useWorldStore((s) => s.projects);

  const chatTab = useMemo(() => {
    if (row.source !== "chat" || row.projectId == null) return null;
    return chatTabs[row.projectId]?.find((t) => t.id === row.nativeId) ?? null;
  }, [row.source, row.nativeId, row.projectId, chatTabs]);

  const chatSession = useMemo(() => {
    if (!chatTab?.sessionId) return null;
    return sessions.get(chatTab.sessionId) ?? null;
  }, [chatTab, sessions]);

  const chatProvider = chatTab?.provider ?? (row.provider as AiProvider) ?? "claude-code";
  const isChatSource = row.source === "chat";
  const isStreaming = chatSession?.state === "running";
  const hasLocalPath = row.projectId != null && !!projects.find((p) => Number(p.id) === row.projectId)?.localPath;
  const chatInputDisabled = !hasLocalPath && !chatSession;

  const handleChatSend = useCallback(async (message: string, _attachments: ChatAttachment[]) => {
    if (!chatTab) return;
    try {
      if (chatTab.sessionId && chatSession) {
        await sendMessage(chatTab.sessionId, message);
      } else {
        const project = projects.find((p) => Number(p.id) === row.projectId);
        const localPath = project?.localPath;
        if (!localPath) return;
        const sessionId = await createSession(chatProvider, localPath, message);
        linkSession(chatTab.id, sessionId);
        const name = message.length > 40 ? message.slice(0, 37) + "..." : message;
        renameTab(chatTab.id, name);
      }
    } catch (err) {
      console.error("[MyAgentsListView] chat send failed:", err);
    }
  }, [chatTab, chatSession, row.projectId, projects, chatProvider, sendMessage, createSession, linkSession, renameTab]);

  const handleChatStop = useCallback(() => {
    if (chatTab?.sessionId) stopSession(chatTab.sessionId);
  }, [chatTab, stopSession]);

  return (
    <div
      role="complementary"
      aria-label="Agent details"
      className="w-[400px] shrink-0 border-l border-[rgba(255,255,255,0.06)] flex flex-col overflow-hidden"
      style={{ background: "var(--colony-content-bg)" }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 h-12 border-b border-[rgba(255,255,255,0.06)] shrink-0">
        <StatusDot status={row.status} />
        <span className="flex-1 text-[14px] font-medium text-colony-text-primary truncate">
          {row.title}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={onDelete}
            aria-label="Delete agent"
            disabled={row.status === "running"}
            className="w-6 h-6 rounded-[4px] flex items-center justify-center text-colony-text-tertiary hover:text-colony-red hover:bg-colony-red/10 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
          >
            <Trash2 size={13} strokeWidth={1.5} />
          </button>
          <button
            onClick={onClose}
            aria-label="Close details panel"
            className="w-6 h-6 rounded-[4px] flex items-center justify-center text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Body: chat conversation or metadata */}
      {isChatSource ? (
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <ChatConversation
            session={chatSession}
            provider={chatProvider}
            isSessionRunning={isStreaming}
            onQuickAction={(prompt) => handleChatSend(prompt, [])}
          />
          <ChatInput
            onSend={handleChatSend}
            onStop={handleChatStop}
            isStreaming={isStreaming ?? false}
            disabled={chatInputDisabled}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-5 py-4" style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <MetaField label="Source" value={
              <div className="flex items-center gap-1.5">
                <SourceBadge source={row.source} />
                <span className="text-[12px] text-colony-text-body">{SOURCE_CONFIG[row.source].label}</span>
              </div>
            } />
            <MetaField label="Status" value={
              <div className="flex items-center gap-1.5">
                <StatusDot status={row.status} />
                <span className="text-[12px] text-colony-text-body">{statusCfg.label}</span>
              </div>
            } />
            <MetaField label="Agent" value={<AgentCell row={row} />} />
            <MetaField label="Project" value={
              row.projectId == null ? (
                <span className="text-[12px] text-colony-text-tertiary italic">Not linked to a project</span>
              ) : (
                <span className="text-[12px] text-colony-text-body">{row.projectName}</span>
              )
            } />
            <MetaField label="Cost" value={<span className="text-[12px] font-mono text-colony-text-body">{formatCost(row.costUsd)}</span>} />
            <MetaField label="Last Activity" value={<span className="text-[12px] font-mono text-colony-text-body">{formatRelativeTime(row.lastActivityAt)}</span>} />
            {row.cwd && (
              <MetaField label="Directory" value={<span className="text-[11px] font-mono text-colony-text-body truncate block">{row.cwd}</span>} />
            )}
          </div>

          {/* Actions */}
          <div className="h-px bg-[rgba(255,255,255,0.06)] my-5" />
          <h3 className="text-[11px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em] mb-3">Actions</h3>
          <div className="flex flex-wrap gap-2">
            {ACTION_HANDLERS[row.source].focus && (
              <DetailAction
                icon={<Focus size={13} strokeWidth={1.5} />}
                label="Focus"
                color="green"
                onClick={() => ACTION_HANDLERS[row.source].focus?.(row)}
              />
            )}
            {ACTION_HANDLERS[row.source].stop && row.status === "running" && (
              <DetailAction
                icon={<Pause size={13} strokeWidth={1.5} />}
                label="Stop"
                color="amber"
                onClick={() => ACTION_HANDLERS[row.source].stop?.(row)}
              />
            )}
            {ACTION_HANDLERS[row.source].resume && (row.status === "completed" || row.status === "error") && (
              <DetailAction
                icon={<Play size={13} strokeWidth={1.5} />}
                label="Resume"
                color="muted"
                onClick={() => ACTION_HANDLERS[row.source].resume?.(row)}
              />
            )}
            {ACTION_HANDLERS[row.source].open_folder && row.cwd && (
              <DetailAction
                icon={<FolderOpen size={13} strokeWidth={1.5} />}
                label="Open Folder"
                color="muted"
                onClick={() => ACTION_HANDLERS[row.source].open_folder?.(row)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetaField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">{label}</span>
      <div className="mt-1">{value}</div>
    </div>
  );
}

function DetailAction({ icon, label, color, onClick }: { icon: React.ReactNode; label: string; color: string; onClick?: () => void }) {
  const colorMap: Record<string, string> = {
    green: "bg-colony-green/10 border-colony-green/20 text-colony-green hover:bg-colony-green/20",
    amber: "bg-colony-amber/10 border-colony-amber/20 text-colony-amber hover:bg-colony-amber/20",
    red: "bg-colony-red/10 border-colony-red/20 text-colony-red hover:bg-colony-red/20",
    muted: "bg-[#222226] border-[rgba(255,255,255,0.06)] text-colony-text-secondary hover:text-colony-text-primary hover:bg-[#2A2A2F]",
  };
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] border text-[11px] font-medium colony-btn-interactive cursor-pointer ${colorMap[color] ?? colorMap.muted}`}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Dropdown Menu (for GroupBy) ──────────────────
function GroupByDropdown({
  trigger,
  items,
  isOpen,
  onToggle,
  onSelect,
}: {
  trigger: React.ReactNode;
  items: { id: string; label: string; active?: boolean }[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] text-[12px] font-medium text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
      >
        {trigger}
      </button>
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 w-40 rounded-[8px] border border-[rgba(255,255,255,0.06)] overflow-hidden z-50"
          style={{
            background: "var(--glass-bg)",
            backdropFilter: "var(--glass-blur)",
            WebkitBackdropFilter: "var(--glass-blur)",
            boxShadow: "var(--colony-shadow-elevated)",
          }}
        >
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                onSelect(item.id);
                onToggle();
              }}
              className={`w-full text-left px-3 py-1.5 text-[12px] transition-colors cursor-pointer ${
                item.active
                  ? "text-[color:var(--colony-brand)] bg-[rgba(0,178,200,0.08)]"
                  : "text-colony-text-secondary hover:text-colony-text-primary hover:bg-[rgba(255,255,255,0.04)]"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────
export function MyAgentsListView({
  viewMode: externalViewMode,
  onViewModeChange,
}: {
  viewMode?: "list" | "board";
  onViewModeChange?: (mode: "list" | "board") => void;
} = {}) {
  const [, navigate] = useLocation();
  useEffect(() => { setAgentActionNavigator(navigate); }, [navigate]);

  const isLoading = useWorldStore((s) => s.isLoading);

  const [sourceFilter, setSourceFilter] = useState<AgentSource | null>(() => {
    const stored = localStorage.getItem("my-agents-source-filter");
    return stored === "workflow" || stored === "chat" || stored === "terminal" ? stored : null;
  });
  const unifiedGroups = useUnifiedAgents(sourceFilter ? { source: sourceFilter } : undefined);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>("project");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [groupByOpen, setGroupByOpen] = useState(false);
  const [internalViewMode, setInternalViewMode] = useState<"list" | "board">("list");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  const allRows = useMemo(() => unifiedGroups.flatMap((g) => g.rows), [unifiedGroups]);
  const groups = useMemo(() => groupRows(unifiedGroups, groupBy), [unifiedGroups, groupBy]);
  const allIds = useMemo(() => allRows.map((r) => r.id), [allRows]);

  const selectedRow = selectedId ? allRows.find((r) => r.id === selectedId) : null;

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleRowFocus = useCallback((row: UnifiedAgentRow) => {
    ACTION_HANDLERS[row.source].focus?.(row);
  }, []);

  const selectAll = useCallback(() => {
    if (selectedIds.size === allIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  }, [allIds, selectedIds.size]);

  const stopSelected = useCallback(async () => {
    const toStop = allRows.filter(
      (r) => selectedIds.has(r.id) && r.status === "running" && ACTION_HANDLERS[r.source].stop,
    );
    await Promise.allSettled(toStop.map((r) => ACTION_HANDLERS[r.source].stop!(r)));
    setSelectedIds(new Set());
  }, [allRows, selectedIds]);

  const deleteSelected = useCallback(async () => {
    const toDelete = allRows.filter(
      (r) => selectedIds.has(r.id) && r.status !== "running" && ACTION_HANDLERS[r.source].delete,
    );
    await Promise.allSettled(toDelete.map((r) => ACTION_HANDLERS[r.source].delete!(r)));
    setSelectedIds(new Set());
    setSelectedId(null);
  }, [allRows, selectedIds]);

  const handleDeleteAgent = useCallback(async (row: UnifiedAgentRow) => {
    if (row.status === "running") return;
    try {
      await ACTION_HANDLERS[row.source].delete?.(row);
      setSelectedId((prev) => (prev === row.id ? null : prev));
    } catch (err) {
      console.error("[MyAgentsListView] delete failed:", err);
    }
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setSelectedId((prev) => {
          const idx = allIds.indexOf(prev ?? "");
          return allIds[Math.min(idx + 1, allIds.length - 1)] ?? allIds[0];
        });
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setSelectedId((prev) => {
          const idx = allIds.indexOf(prev ?? "");
          return allIds[Math.max(idx - 1, 0)] ?? allIds[0];
        });
      } else if (e.key === "Escape") {
        setSelectedId(null);
        setSelectedIds(new Set());
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [allIds]);

  const isAllSelected = selectedIds.size === allIds.length && allIds.length > 0;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < allIds.length;

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Main table area */}
      <div className="flex flex-col flex-1 overflow-hidden min-w-0">
        {/* Page header */}
        <div className="flex items-center h-12 px-5 border-b border-[rgba(255,255,255,0.06)] shrink-0">
          <div className="flex items-center gap-2.5 flex-1">
            <span className="text-[16px] font-semibold text-colony-text-heading tracking-[-0.015em]">
              My Agents
            </span>
            <span className="text-[11px] text-colony-text-tertiary font-mono tabular-nums">
              {allRows.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {/* View toggle */}
            <div className="flex items-center h-7 rounded-[6px] bg-[#1A1A1E] border border-[rgba(255,255,255,0.06)] overflow-hidden mr-2" role="group" aria-label="View mode">
              <button
                onClick={() => setViewMode("list")}
                aria-label="List view"
                aria-pressed={viewMode === "list"}
                className={`flex items-center justify-center w-7 h-full transition-colors cursor-pointer ${
                  viewMode === "list"
                    ? "text-colony-text-primary bg-[#272730]"
                    : "text-colony-text-tertiary hover:text-colony-text-secondary"
                }`}
              >
                <LayoutList size={14} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => setViewMode("board")}
                aria-label="Board view"
                aria-pressed={viewMode === "board"}
                className={`flex items-center justify-center w-7 h-full transition-colors cursor-pointer ${
                  viewMode === "board"
                    ? "text-colony-text-primary bg-[#272730]"
                    : "text-colony-text-tertiary hover:text-colony-text-secondary"
                }`}
              >
                <LayoutGrid size={14} strokeWidth={1.5} />
              </button>
            </div>

            <GroupByDropdown
              trigger={
                <>
                  <Layers size={13} strokeWidth={1.5} />
                  Group
                  <ChevronDown size={10} strokeWidth={2} />
                </>
              }
              items={[
                { id: "none", label: "No grouping", active: groupBy === "none" },
                { id: "project", label: "Project", active: groupBy === "project" },
                { id: "status", label: "Status", active: groupBy === "status" },
                { id: "source", label: "Source", active: groupBy === "source" },
                { id: "agent-type", label: "Agent Type", active: groupBy === "agent-type" },
              ]}
              isOpen={groupByOpen}
              onToggle={() => setGroupByOpen(!groupByOpen)}
              onSelect={(id) => setGroupBy(id as GroupBy)}
            />
          </div>
        </div>

        {/* Source filter pills */}
        <div className="flex items-center gap-1.5 h-9 px-5 border-b border-[rgba(255,255,255,0.06)] shrink-0">
          {(["all", "chat", "terminal", "workflow"] as const).map((src) => {
            const active = src === "all" ? sourceFilter === null : sourceFilter === src;
            const label = src === "all" ? "All" : SOURCE_CONFIG[src].label;
            return (
              <button
                key={src}
                onClick={() => {
                  const next = src === "all" ? null : src;
                  setSourceFilter(next);
                  localStorage.setItem("my-agents-source-filter", next ?? "all");
                }}
                className={`h-6 px-2.5 rounded-full text-[11px] font-medium transition-all cursor-pointer ${
                  active
                    ? "bg-[rgba(0,178,200,0.15)] text-[color:var(--colony-brand)] border border-[rgba(0,178,200,0.3)]"
                    : "text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] border border-transparent"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Column headers */}
        <div className="flex items-center h-8 px-2 border-b border-[rgba(255,255,255,0.06)] shrink-0 bg-colony-surface-elevated">
          <button
            onClick={selectAll}
            aria-label={isAllSelected ? "Deselect all" : "Select all"}
            className="w-8 flex items-center justify-center shrink-0 cursor-pointer text-colony-text-tertiary hover:text-colony-text-secondary"
          >
            {isAllSelected ? (
              <CheckSquare size={14} strokeWidth={1.5} className="text-[color:var(--colony-brand)]" />
            ) : isSomeSelected ? (
              <Minus size={14} strokeWidth={1.5} className="text-[color:var(--colony-brand)]" />
            ) : (
              <Square size={14} strokeWidth={1.5} />
            )}
          </button>
          <div className="w-8 shrink-0" />
          <div className="w-8 shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]" title="Source" />
          <div className="flex-1 min-w-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Title</div>
          <div className="w-[120px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Agent</div>
          <div className="w-[120px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em]">Project</div>
          <div className="w-[64px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em] text-right">Cost</div>
          <div className="w-[64px] shrink-0 text-[10px] font-medium text-colony-text-tertiary uppercase tracking-[0.06em] text-right">Time</div>
        </div>

        {/* Scrollable rows */}
        <div
          ref={tableRef}
          className="flex-1 overflow-y-auto"
          style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}
        >
          {isLoading && allRows.length === 0 && (
            <div className="animate-pulse">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center h-10 px-2 border-b border-[rgba(255,255,255,0.06)]">
                  <div className="w-8 shrink-0" />
                  <div className="w-8 flex items-center justify-center shrink-0">
                    <div className="w-2 h-2 rounded-full bg-[#2A2A2F]" />
                  </div>
                  <div className="w-8 flex items-center justify-center shrink-0">
                    <div className="w-3.5 h-3.5 rounded-[3px] bg-[#2A2A2F]" />
                  </div>
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="h-3 rounded bg-[#2A2A2F]" style={{ width: `${50 + (i * 13) % 40}%` }} />
                  </div>
                  <div className="w-[120px] shrink-0">
                    <div className="h-3 w-16 rounded bg-[#2A2A2F]" />
                  </div>
                  <div className="w-[120px] shrink-0">
                    <div className="h-3 w-20 rounded bg-[#2A2A2F]" />
                  </div>
                  <div className="w-[64px] shrink-0 flex justify-end">
                    <div className="h-3 w-10 rounded bg-[#2A2A2F]" />
                  </div>
                  <div className="w-[64px] shrink-0 flex justify-end">
                    <div className="h-3 w-6 rounded bg-[#2A2A2F]" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!isLoading && allRows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-colony-text-tertiary">
              <span className="text-[14px] font-medium mb-1">No agents active</span>
              <span className="text-[12px]">Start a chat, terminal, or workflow to see agents here</span>
            </div>
          )}
          {groups.map((group) => (
            <div key={group.key}>
              {groupBy !== "none" && (
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="flex items-center gap-2 w-full h-8 px-3 bg-[#111114] border-b border-[rgba(255,255,255,0.04)] cursor-pointer group/header hover:bg-[#151518] transition-colors"
                >
                  {collapsedGroups.has(group.key) ? (
                    <ChevronRight size={12} strokeWidth={2} className="text-colony-text-tertiary" />
                  ) : (
                    <ChevronDown size={12} strokeWidth={2} className="text-colony-text-tertiary" />
                  )}
                  {groupBy === "source" && (
                    <SourceBadge source={group.key as AgentSource} />
                  )}
                  {groupBy === "agent-type" && AGENT_TYPE_CONFIG[group.key as AgentType] && (
                    <AgentTypeAvatar type={group.key as AgentType} size={16} />
                  )}
                  <span className="text-[11px] font-medium text-colony-text-secondary">
                    {group.label}
                  </span>
                  <span className="text-[10px] text-colony-text-tertiary font-mono tabular-nums">
                    {group.rows.length}
                  </span>
                </button>
              )}

              {!collapsedGroups.has(group.key) &&
                group.rows.map((row) => {
                  const isSelected = selectedId === row.id;
                  const isChecked = selectedIds.has(row.id);
                  const isHovered = hoveredId === row.id;

                  return (
                    <div
                      key={row.id}
                      onClick={() => setSelectedId(row.id === selectedId ? null : row.id)}
                      onDoubleClick={() => handleRowFocus(row)}
                      onMouseEnter={() => setHoveredId(row.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      className={`flex items-center h-10 px-2 border-b border-[rgba(255,255,255,0.06)] cursor-pointer transition-colors duration-75 ${
                        isSelected
                          ? "bg-[rgba(0,178,200,0.08)]"
                          : isChecked
                          ? "bg-[rgba(0,178,200,0.04)]"
                          : "hover:bg-[rgba(255,255,255,0.03)]"
                      }`}
                    >
                      <div
                        className="w-8 flex items-center justify-center shrink-0"
                        onClick={(e) => toggleSelect(row.id, e)}
                      >
                        {isChecked ? (
                          <CheckSquare size={14} strokeWidth={1.5} className="text-[color:var(--colony-brand)] cursor-pointer" />
                        ) : isHovered || selectedIds.size > 0 ? (
                          <Square size={14} strokeWidth={1.5} className="text-colony-text-tertiary hover:text-colony-text-secondary cursor-pointer" />
                        ) : (
                          <div className="w-[14px]" />
                        )}
                      </div>

                      <div className="w-8 flex items-center justify-center shrink-0">
                        <StatusDot status={row.status} />
                      </div>

                      <div className="w-8 flex items-center justify-center shrink-0">
                        <SourceBadge source={row.source} />
                      </div>

                      <div className="flex-1 min-w-0 pr-3">
                        <span className="text-[13px] font-medium text-colony-text-primary truncate block">
                          {row.title}
                        </span>
                      </div>

                      <div className="w-[120px] shrink-0">
                        <AgentCell row={row} />
                      </div>

                      <div className="w-[120px] shrink-0">
                        <span className="text-[12px] text-colony-text-tertiary truncate block">
                          {row.projectName}
                        </span>
                      </div>

                      <div className="w-[64px] shrink-0 text-right">
                        <span className="text-[12px] font-mono text-colony-text-secondary tabular-nums">
                          {formatCost(row.costUsd)}
                        </span>
                      </div>

                      <div className="w-[64px] shrink-0 text-right">
                        <span className="text-[12px] font-mono text-colony-text-tertiary tabular-nums">
                          {formatRelativeTime(row.lastActivityAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>

        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <div
            className="flex items-center gap-3 h-11 px-5 border-t border-[rgba(255,255,255,0.06)] shrink-0"
            style={{
              background: "var(--glass-bg)",
              backdropFilter: "var(--glass-blur)",
              WebkitBackdropFilter: "var(--glass-blur)",
            }}
          >
            <span className="text-[12px] font-medium text-colony-text-primary">
              {selectedIds.size} selected
            </span>
            <div className="w-px h-4 bg-[rgba(255,255,255,0.06)]" />
            <button
              onClick={stopSelected}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] bg-colony-amber/10 border border-colony-amber/20 text-[11px] font-medium text-colony-amber hover:bg-colony-amber/20 colony-btn-interactive cursor-pointer"
            >
              <Pause size={12} strokeWidth={1.5} />
              Stop All
            </button>
            <button
              onClick={deleteSelected}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-[6px] bg-colony-red/10 border border-colony-red/20 text-[11px] font-medium text-colony-red hover:bg-colony-red/20 colony-btn-interactive cursor-pointer"
            >
              <Trash2 size={12} strokeWidth={1.5} />
              Delete
            </button>
            <div className="flex-1" />
            <button
              onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-1 h-7 px-2 rounded-[6px] text-[11px] font-medium text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[rgba(255,255,255,0.04)] transition-all cursor-pointer"
            >
              <X size={12} strokeWidth={1.5} />
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedRow && (
        <DetailPanel
          row={selectedRow}
          onClose={() => setSelectedId(null)}
          onDelete={() => handleDeleteAgent(selectedRow)}
        />
      )}
    </div>
  );
}
